import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Radio,
  InputNumber,
  Divider,
  Alert,
  Checkbox,
  Tooltip,
  Badge,
  Progress,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ImportOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
  KeyOutlined,
  TrophyOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
  QuestionCircleOutlined,
  FormOutlined,
  ScanOutlined,
  CheckCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import desktopQuizAPI from '../../services/desktopQuizAPI';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { resolveOperationSchoolId } from '../../utils/schoolScope';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// 科目选项
const SUBJECTS = [
  '语文', '数学', '英语', '物理', '化学', '生物',
  '历史', '地理', '政治', '道德与法治', '科学', '综合'
];

// 年级选项
const GRADES = [
  '一年级', '二年级', '三年级', '四年级', '五年级', '六年级',
  '七年级', '八年级', '九年级', '高一', '高二', '高三'
];

// 题型选项
const QUESTION_TYPES = [
  { value: 'single', label: '单选题', color: 'blue' },
  { value: 'multiple', label: '多选题', color: 'purple' },
  { value: 'truefalse', label: '判断题', color: 'orange' },
  { value: 'fill', label: '填空题', color: 'green' }
];

/**
 * 桌面客户端在线答题管理
 */
const DesktopQuizManager = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('questions');
  const [pageTitle] = useState('桌面客户端答题');
  
  // 题库相关状态
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [questionForm] = Form.useForm();
  const [importForm] = Form.useForm();
  const [pushModalVisible, setPushModalVisible] = useState(false);
  const [pushForm] = Form.useForm();
  
  // 筛选条件
  const [filters, setFilters] = useState({
    source: 'all',
    subject: null,
    grade: null,
    questionType: null,
    keyword: ''
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // 班级和学校数据
  const [classes, setClasses] = useState([]);
  
  // 操作码相关状态
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [studentCodes, setStudentCodes] = useState([]);
  const [codeSettings, setCodeSettings] = useState(null);
  const [codesLoading, setCodesLoading] = useState(false);
  
  // 发起答题相关状态
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [sessionForm] = Form.useForm();
  const [scoreTemplates, setScoreTemplates] = useState([]);
  
  // 排行榜相关状态
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // 预览解析结果
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 智能识别相关状态
  const [recognizeText, setRecognizeText] = useState('');
  const [recognizedQuestion, setRecognizedQuestion] = useState(null);
  const [recognizeLoading, setRecognizeLoading] = useState(false);

  // 打开推送对话框
  const handleOpenPushModal = () => {
    if (selectedQuestionIds.length === 0) {
      message.warning('请先选择题目');
      return;
    }
    pushForm.resetFields();
    pushForm.setFieldsValue({
      class_id: selectedClassId || classes[0]?.id,
      allow_class_start: true
    });
    setPushModalVisible(true);
  };

  // 提交推送
  const handleSubmitPush = async () => {
    try {
      const values = await pushForm.validateFields();
      const payload = {
        class_id: values.class_id,
        question_ids: selectedQuestionIds,
        allow_class_start: values.allow_class_start || false,
        title: values.title || undefined,
        schedule_at: values.schedule_at ? values.schedule_at.format('YYYY-MM-DD HH:mm:ss') : null
      };
      const resp = await desktopQuizAPI.pushQuestions(payload);
      if (resp.data.success) {
        message.success(resp.data.message || '推送成功');
        setPushModalVisible(false);
      }
    } catch (error) {
      if (!error.errorFields) {
        message.error(error.response?.data?.message || '推送失败');
      }
    }
  };

  useEffect(() => {
    loadClasses();
    loadScoreTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === 'questions') {
      loadQuestions();
    }
  }, [activeTab, filters, pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (activeTab === 'codes' && selectedClassId) {
      loadStudentCodes(selectedClassId);
    }
  }, [activeTab, selectedClassId]);

  useEffect(() => {
    if (activeTab === 'leaderboard' && selectedClassId) {
      loadLeaderboard(selectedClassId);
    }
  }, [activeTab, selectedClassId]);

  // 加载班级列表
  const loadClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        setClasses(response.data.data || []);
        if (response.data.data.length > 0) {
          setSelectedClassId(response.data.data[0].id);
        }
      }
    } catch (error) {
      console.error('加载班级列表失败:', error);
    }
  };

  // 加载积分模板
  const loadScoreTemplates = async () => {
    try {
      const response = await desktopQuizAPI.getScoreTemplates({
        teacherId: user?.id
      });
      if (response.data.success) {
        setScoreTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('加载积分模板失败:', error);
    }
  };

  // 加载题目列表
  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await desktopQuizAPI.getQuestions({
        teacherId: user?.id,
        schoolId: resolveOperationSchoolId({ classes, classId: selectedClassId, user }),
        ...filters,
        page: pagination.current,
        pageSize: pagination.pageSize
      });
      if (response.data.success) {
        setQuestions(response.data.data.list || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.total || 0
        }));
      }
    } catch (error) {
      console.error('加载题目列表失败:', error);
      message.error('加载题目列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载学生操作码
  const loadStudentCodes = async (classId) => {
    try {
      setCodesLoading(true);
      const response = await desktopQuizAPI.getStudentCodes(classId);
      if (response.data.success) {
        setStudentCodes(response.data.data.codes || []);
        setCodeSettings(response.data.data.settings);
      }
    } catch (error) {
      console.error('加载操作码失败:', error);
      message.error('加载操作码失败');
    } finally {
      setCodesLoading(false);
    }
  };

  // 加载排行榜
  const loadLeaderboard = async (classId) => {
    try {
      setLeaderboardLoading(true);
      const response = await desktopQuizAPI.getLeaderboard(classId, { limit: 50 });
      if (response.data.success) {
        setLeaderboard(response.data.data || []);
      }
    } catch (error) {
      console.error('加载排行榜失败:', error);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // 打开新建/编辑题目对话框
  const handleOpenQuestionModal = (question = null) => {
    setEditingQuestion(question);
    setQuestionModalVisible(true);

    if (question) {
      questionForm.setFieldsValue({
        ...question,
        options: question.options || []
      });
    } else {
      questionForm.resetFields();
      questionForm.setFieldsValue({
        question_type: 'single',
        difficulty: 'medium',
        is_shared: 0,
        options: [
          { key: 'A', label: '' },
          { key: 'B', label: '' },
          { key: 'C', label: '' },
          { key: 'D', label: '' }
        ]
      });
    }
  };

  // 保存题目
  const handleSaveQuestion = async () => {
    try {
      const values = await questionForm.validateFields();
      
      const data = {
        ...values,
        teacher_id: user?.id,
        school_id: resolveOperationSchoolId({
          classes,
          classId: values.class_id || selectedClassId,
          item: editingQuestion,
          user
        })
      };

      if (editingQuestion) {
        await desktopQuizAPI.updateQuestion(editingQuestion.id, data);
        message.success('更新成功');
      } else {
        await desktopQuizAPI.createQuestion(data);
        message.success('创建成功');
      }

      setQuestionModalVisible(false);
      setEditingQuestion(null);
      setRecognizeText('');
      setRecognizedQuestion(null);
      loadQuestions();
    } catch (error) {
      if (!error.errorFields) {
        console.error('保存失败:', error);
        message.error('保存失败');
      }
    }
  };

  // 智能识别题目（前端本地解析）
  const handleRecognizeQuestion = () => {
    if (!recognizeText.trim()) {
      message.warning('请先粘贴题目内容');
      return;
    }

    setRecognizeLoading(true);
    
    try {
      const text = recognizeText.trim();
      const result = parseQuestionText(text);
      
      if (result) {
        setRecognizedQuestion(result);
        message.success('识别成功！');
      } else {
        message.warning('无法识别题目格式，请检查内容或手动填写');
      }
    } catch (error) {
      console.error('识别失败:', error);
      message.error('识别失败，请手动填写');
    } finally {
      setRecognizeLoading(false);
    }
  };

  // 前端题目解析函数
  const parseQuestionText = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    if (lines.length === 0) return null;

    let questionText = '';
    let options = [];
    let correctAnswer = '';
    let questionType = 'single';

    // 提取答案
    const answerPatterns = [
      /^答案[：:]\s*(.+)$/i,
      /^正确答案[：:]\s*(.+)$/i,
      /^【答案】\s*(.+)$/i,
      /^参考答案[：:]\s*(.+)$/i
    ];

    let answerLineIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      for (const pattern of answerPatterns) {
        const match = lines[i].match(pattern);
        if (match) {
          correctAnswer = match[1].trim();
          answerLineIndex = i;
          break;
        }
      }
      if (answerLineIndex >= 0) break;
    }

    // 移除答案行
    const contentLines = answerLineIndex >= 0 
      ? lines.filter((_, i) => i !== answerLineIndex)
      : lines;

    // 提取选项（支持多种格式）
    const optionPatterns = [
      /^([A-F])[.、．)\s]+(.+)$/i,  // A. 选项 或 A、选项 或 A）选项
      /^([A-F])[：:]\s*(.+)$/i      // A：选项
    ];

    const optionLines = [];
    const nonOptionLines = [];

    for (const line of contentLines) {
      let isOption = false;
      for (const pattern of optionPatterns) {
        const match = line.match(pattern);
        if (match) {
          optionLines.push({ key: match[1].toUpperCase(), label: match[2].trim() });
          isOption = true;
          break;
        }
      }
      if (!isOption) {
        nonOptionLines.push(line);
      }
    }

    // 处理同一行多个选项的情况（如：A.选项1  B.选项2  C.选项3  D.选项4）
    if (optionLines.length === 0) {
      for (const line of contentLines) {
        const inlineOptions = line.match(/([A-F])[.、．)\s]+([^A-F]+?)(?=\s+[A-F][.、．)\s]|$)/gi);
        if (inlineOptions && inlineOptions.length >= 2) {
          for (const opt of inlineOptions) {
            const match = opt.match(/([A-F])[.、．)\s]+(.+)/i);
            if (match) {
              optionLines.push({ key: match[1].toUpperCase(), label: match[2].trim() });
            }
          }
          // 从非选项行中移除这一行
          const idx = nonOptionLines.indexOf(line);
          if (idx >= 0) nonOptionLines.splice(idx, 1);
        }
      }
    }

    options = optionLines;

    // 题目内容：移除题号前缀
    questionText = nonOptionLines.join(' ').trim();
    questionText = questionText.replace(/^[\d一二三四五六七八九十]+[.、．)\s]+/, '').trim();
    questionText = questionText.replace(/^【[^】]+】\s*/, '').trim();
    questionText = questionText.replace(/^（[^）]+）\s*/, '').trim();
    questionText = questionText.replace(/\([单多][选项]?\)$/i, '').trim();
    questionText = questionText.replace(/（[单多][选项]?）$/i, '').trim();

    // 判断题型
    if (options.length === 0) {
      // 判断题检测
      const tfPatterns = [
        /[（\(]\s*[）\)]/,  // 括号判断
        /判断题/i,
        /正确|错误|对|错/
      ];
      const isTrueFalse = tfPatterns.some(p => p.test(text)) || 
                          ['对', '错', '正确', '错误', '√', '×', 'T', 'F', 'true', 'false'].includes(correctAnswer.toLowerCase());
      
      if (isTrueFalse) {
        questionType = 'truefalse';
        // 标准化判断题答案
        if (['对', '正确', '√', 'T', 'true', '是'].includes(correctAnswer.toLowerCase())) {
          correctAnswer = '对';
        } else if (['错', '错误', '×', 'F', 'false', '否'].includes(correctAnswer.toLowerCase())) {
          correctAnswer = '错';
        }
      } else {
        // 填空题检测
        if (questionText.includes('____') || questionText.includes('___') || questionText.includes('（  ）')) {
          questionType = 'fill';
        } else {
          questionType = 'fill'; // 默认填空题
        }
      }
    } else {
      // 有选项，判断单选还是多选
      if (correctAnswer.length > 1 && /^[A-F]+$/i.test(correctAnswer)) {
        questionType = 'multiple';
      } else {
        questionType = 'single';
      }
    }

    // 如果没有识别到题目内容，返回null
    if (!questionText) return null;

    return {
      question_type: questionType,
      question_text: questionText,
      options: options.length > 0 ? options : null,
      correct_answer: correctAnswer || ''
    };
  };

  // 应用识别结果到表单
  const handleApplyRecognized = () => {
    if (!recognizedQuestion) return;

    const formData = {
      question_type: recognizedQuestion.question_type,
      question_text: recognizedQuestion.question_text,
      correct_answer: recognizedQuestion.correct_answer,
      difficulty: 'medium',
      is_shared: 0
    };

    if (recognizedQuestion.options && recognizedQuestion.options.length > 0) {
      formData.options = recognizedQuestion.options;
    } else if (recognizedQuestion.question_type === 'single' || recognizedQuestion.question_type === 'multiple') {
      formData.options = [
        { key: 'A', label: '' },
        { key: 'B', label: '' },
        { key: 'C', label: '' },
        { key: 'D', label: '' }
      ];
    }

    questionForm.setFieldsValue(formData);
    message.success('已应用到表单');
  };

  // 删除题目
  const handleDeleteQuestion = async (id) => {
    try {
      await desktopQuizAPI.deleteQuestion(id);
      message.success('删除成功');
      loadQuestions();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedQuestionIds.length === 0) {
      message.warning('请选择要删除的题目');
      return;
    }
    try {
      await desktopQuizAPI.batchDeleteQuestions(selectedQuestionIds);
      message.success(`成功删除 ${selectedQuestionIds.length} 道题目`);
      setSelectedQuestionIds([]);
      loadQuestions();
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  // 预览导入
  const handlePreviewImport = async () => {
    try {
      const text = importForm.getFieldValue('text');
      if (!text) {
        message.warning('请输入题目内容');
        return;
      }
      
      setPreviewLoading(true);
      const response = await desktopQuizAPI.previewQuestions(text);
      if (response.data.success) {
        setPreviewResult(response.data.data);
      }
    } catch (error) {
      message.error('预览解析失败');
    } finally {
      setPreviewLoading(false);
    }
  };

  // 确认导入
  const handleConfirmImport = async () => {
    try {
      const values = await importForm.validateFields();
      
      const response = await desktopQuizAPI.importQuestions({
        text: values.text,
        teacher_id: user?.id,
        school_id: resolveOperationSchoolId({ classes, classId: selectedClassId, user }),
        subject: values.subject,
        grade: values.grade,
        is_shared: values.is_shared || 0
      });

      if (response.data.success) {
        message.success(`成功导入 ${response.data.data.imported} 道题目`);
        setImportModalVisible(false);
        setPreviewResult(null);
        importForm.resetFields();
        loadQuestions();
      }
    } catch (error) {
      message.error('导入失败');
    }
  };

  // 批量生成操作码
  const handleGenerateCodes = async () => {
    if (!selectedClassId) {
      message.warning('请选择班级');
      return;
    }

    try {
      const response = await desktopQuizAPI.generateCodes({
        classId: selectedClassId,
        codeLength: 3,
        validityType: 'permanent'
      });
      if (response.data.success) {
        message.success(response.data.message);
        loadStudentCodes(selectedClassId);
      }
    } catch (error) {
      message.error('生成操作码失败');
    }
  };

  // 重新生成单个操作码
  const handleRegenerateCode = async (studentId) => {
    try {
      const response = await desktopQuizAPI.regenerateCode(studentId);
      if (response.data.success) {
        message.success('操作码已更新');
        loadStudentCodes(selectedClassId);
      }
    } catch (error) {
      message.error('更新操作码失败');
    }
  };

  // 打开发起答题对话框
  const handleOpenSessionModal = () => {
    if (selectedQuestionIds.length === 0) {
      message.warning('请先选择题目');
      return;
    }
    setSessionModalVisible(true);
    sessionForm.setFieldsValue({
      class_id: selectedClassId,
      answer_mode: 'normal',
      max_attempts: 1,
      time_per_question: 30,
      show_answer_after: 'immediate',
      score_correct: 10,
      score_wrong: 0,
      score_race_bonus: 5
    });
  };

  // 创建并开始答题
  const handleStartSession = async () => {
    try {
      const values = await sessionForm.validateFields();
      
      // 创建会话
      const createResponse = await desktopQuizAPI.createSession({
        teacher_id: user?.id,
        class_id: values.class_id,
        title: values.title,
        question_ids: selectedQuestionIds,
        ...values
      });

      if (createResponse.data.success) {
        const sessionId = createResponse.data.data.sessionId;
        
        // 开始答题
        const startResponse = await desktopQuizAPI.startSession(sessionId);
        if (startResponse.data.success) {
          message.success('答题已推送到桌面客户端');
          setSessionModalVisible(false);
          setSelectedQuestionIds([]);
        }
      }
    } catch (error) {
      message.error('发起答题失败');
    }
  };

  // 题目表格列
  const questionColumns = [
    {
      title: '题目内容',
      dataIndex: 'question_text',
      key: 'question_text',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text ellipsis style={{ maxWidth: 300 }}>{text}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color={QUESTION_TYPES.find(t => t.value === record.question_type)?.color || 'default'}>
              {QUESTION_TYPES.find(t => t.value === record.question_type)?.label || record.question_type}
            </Tag>
            {record.correct_answer && (
              <Tag color="green">答案: {record.correct_answer}</Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: '科目/年级',
      key: 'category',
      width: 120,
      render: (_, record) => (
        <div>
          {record.subject && <Tag>{record.subject}</Tag>}
          {record.grade && <Tag>{record.grade}</Tag>}
        </div>
      )
    },
    {
      title: '难度',
      dataIndex: 'difficulty',
      key: 'difficulty',
      width: 80,
      render: (difficulty) => {
        const colors = { easy: 'green', medium: 'orange', hard: 'red' };
        const labels = { easy: '简单', medium: '中等', hard: '困难' };
        return <Tag color={colors[difficulty]}>{labels[difficulty]}</Tag>;
      }
    },
    {
      title: '共享',
      dataIndex: 'is_shared',
      key: 'is_shared',
      width: 80,
      render: (isShared) => {
        const labels = { 0: '私有', 1: '本校', 2: '公开' };
        const colors = { 0: 'default', 1: 'blue', 2: 'green' };
        return <Tag color={colors[isShared]}>{labels[isShared]}</Tag>;
      }
    },
    {
      title: '使用次数',
      dataIndex: 'use_count',
      key: 'use_count',
      width: 80,
      render: (count) => <Badge count={count} showZero overflowCount={999} />
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenQuestionModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除这道题目吗？"
            onConfirm={() => handleDeleteQuestion(record.id)}
          >
            <Tooltip title="删除">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 操作码表格列
  const codeColumns = [
    {
      title: '学号',
      dataIndex: 'student_number',
      key: 'student_number',
      width: 100
    },
    {
      title: '姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100
    },
    {
      title: '操作码',
      dataIndex: 'operation_code',
      key: 'operation_code',
      width: 100,
      render: (code) => (
        <Tag color="blue" style={{ fontSize: 16, fontWeight: 'bold' }}>{code}</Tag>
      )
    },
    {
      title: '有效期',
      dataIndex: 'validity_type',
      key: 'validity_type',
      width: 100,
      render: (type) => {
        const labels = {
          permanent: '长期有效',
          daily: '每日更新',
          weekly: '每周更新',
          monthly: '每月更新',
          semester: '每学期更新'
        };
        return <Tag>{labels[type] || type}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => handleRegenerateCode(record.student_id)}
        >
          重新生成
        </Button>
      )
    }
  ];

  // 排行榜表格列
  const leaderboardColumns = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank) => {
        if (rank === 1) return <span style={{ fontSize: 20 }}>🥇</span>;
        if (rank === 2) return <span style={{ fontSize: 20 }}>🥈</span>;
        if (rank === 3) return <span style={{ fontSize: 20 }}>🥉</span>;
        return <Text strong>{rank}</Text>;
      }
    },
    {
      title: '姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100
    },
    {
      title: '总积分',
      dataIndex: 'total_score',
      key: 'total_score',
      width: 100,
      render: (score) => <Text strong style={{ color: '#52c41a' }}>{score}</Text>
    },
    {
      title: '答对',
      dataIndex: 'correct_count',
      key: 'correct_count',
      width: 80
    },
    {
      title: '答错',
      dataIndex: 'wrong_count',
      key: 'wrong_count',
      width: 80
    },
    {
      title: '正确率',
      dataIndex: 'accuracy_rate',
      key: 'accuracy_rate',
      width: 120,
      render: (rate) => <Progress percent={parseFloat(rate)} size="small" />
    },
    {
      title: '抢答成功',
      dataIndex: 'race_win_count',
      key: 'race_win_count',
      width: 80
    },
    {
      title: '最长连对',
      dataIndex: 'max_streak',
      key: 'max_streak',
      width: 80
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <FormOutlined style={{ marginRight: 8 }} /> 桌面客户端答题
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
              管理桌面客户端在线答题，支持单选、多选、判断、填空题型。学生通过操作码在桌面客户端提交答案。
            </Paragraph>
          </Col>
        </Row>
      </Card>
      
      <Card>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* 题库管理 */}
        <TabPane 
          tab={<span><QuestionCircleOutlined /> 题库管理</span>} 
          key="questions"
        >
          <Card>
            {/* 筛选条件 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={4}>
                <Select
                  placeholder="来源"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.source}
                  onChange={(value) => setFilters({ ...filters, source: value || 'all' })}
                >
                  <Option value="all">全部</Option>
                  <Option value="mine">我的题目</Option>
                  <Option value="shared">共享题目</Option>
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="科目"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.subject}
                  onChange={(value) => setFilters({ ...filters, subject: value })}
                >
                  {SUBJECTS.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="年级"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.grade}
                  onChange={(value) => setFilters({ ...filters, grade: value })}
                >
                  {GRADES.map(g => <Option key={g} value={g}>{g}</Option>)}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="题型"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.questionType}
                  onChange={(value) => setFilters({ ...filters, questionType: value })}
                >
                  {QUESTION_TYPES.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                </Select>
              </Col>
              <Col span={8}>
                <Input.Search
                  placeholder="搜索题目内容"
                  allowClear
                  onSearch={(value) => setFilters({ ...filters, keyword: value })}
                />
              </Col>
            </Row>

            {/* 操作按钮 */}
            <Row justify="space-between" style={{ marginBottom: 16 }}>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenQuestionModal()}
                  >
                    添加题目
                  </Button>
                  <Button
                    icon={<ImportOutlined />}
                    onClick={() => setImportModalVisible(true)}
                  >
                    批量导入
                  </Button>
                  {selectedQuestionIds.length > 0 && (
                    <>
                      <Button
                        icon={<SendOutlined />}
                        onClick={handleOpenPushModal}
                      >
                        推送题目 ({selectedQuestionIds.length}题)
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={handleOpenSessionModal}
                      >
                        发起答题 ({selectedQuestionIds.length}题)
                      </Button>
                      <Popconfirm
                        title={`确定删除选中的 ${selectedQuestionIds.length} 道题目吗？`}
                        onConfirm={handleBatchDelete}
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          批量删除
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                </Space>
              </Col>
              <Col>
                <Text type="secondary">共 {pagination.total} 道题目</Text>
              </Col>
            </Row>

            {/* 题目表格 */}
            <Table
              columns={questionColumns}
              dataSource={questions}
              rowKey="id"
              loading={loading}
              rowSelection={{
                selectedRowKeys: selectedQuestionIds,
                onChange: setSelectedQuestionIds
              }}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize })
              }}
            />
          </Card>
        </TabPane>

        {/* 操作码管理 */}
        <TabPane 
          tab={<span><KeyOutlined /> 操作码管理</span>} 
          key="codes"
        >
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Select
                  placeholder="选择班级"
                  style={{ width: '100%' }}
                  value={selectedClassId}
                  onChange={setSelectedClassId}
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={handleGenerateCodes}
                >
                  批量生成操作码
                </Button>
              </Col>
            </Row>

            {codeSettings && (
              <Alert
                message={`当前设置: ${codeSettings.code_length}位操作码, ${
                  { permanent: '长期有效', daily: '每日更新', weekly: '每周更新', monthly: '每月更新', semester: '每学期更新' }[codeSettings.validity_type]
                }`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Table
              columns={codeColumns}
              dataSource={studentCodes}
              rowKey="id"
              loading={codesLoading}
              pagination={{ pageSize: 20 }}
            />
          </Card>
        </TabPane>

        {/* 积分排行榜 */}
        <TabPane 
          tab={<span><TrophyOutlined /> 积分排行榜</span>} 
          key="leaderboard"
        >
          <Card>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Select
                  placeholder="选择班级"
                  style={{ width: '100%' }}
                  value={selectedClassId}
                  onChange={(value) => {
                    setSelectedClassId(value);
                    loadLeaderboard(value);
                  }}
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Table
              columns={leaderboardColumns}
              dataSource={leaderboard}
              rowKey="student_id"
              loading={leaderboardLoading}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 添加/编辑题目对话框 */}
      <Modal
        title={editingQuestion ? '编辑题目' : '添加题目'}
        open={questionModalVisible}
        onOk={handleSaveQuestion}
        onCancel={() => {
          setQuestionModalVisible(false);
          setEditingQuestion(null);
          setRecognizeText('');
          setRecognizedQuestion(null);
        }}
        width={900}
        okText="保存"
        cancelText="取消"
      >
        {/* 智能识别输入区域 - 仅在新建时显示 */}
        {!editingQuestion && (
          <Card 
            size="small" 
            title={
              <Space>
                <ScanOutlined style={{ color: '#1890ff' }} />
                <span>智能识别模式</span>
                <Tag color="blue">从Word/文本粘贴</Tag>
              </Space>
            }
            style={{ marginBottom: 16, background: '#f6f8fa' }}
          >
            <TextArea
              value={recognizeText}
              onChange={(e) => setRecognizeText(e.target.value)}
              rows={5}
              placeholder={`粘贴题目内容，系统将自动识别题目、选项和答案

支持格式示例：
1. 下列哪个是中国的首都？
A. 上海  B. 北京  C. 广州  D. 深圳
答案：B

或者：
地球是太阳系中最大的行星。（  ）
答案：错

或者：
中国的母亲河是____。
答案：黄河`}
              style={{ fontFamily: 'monospace' }}
            />
            <Row justify="space-between" align="middle" style={{ marginTop: 12 }}>
              <Col>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<ScanOutlined />}
                    onClick={handleRecognizeQuestion}
                    loading={recognizeLoading}
                  >
                    识别题目
                  </Button>
                  <Button onClick={() => { setRecognizeText(''); setRecognizedQuestion(null); }}>
                    清空
                  </Button>
                </Space>
              </Col>
              <Col>
                <Text type="secondary">
                  提示：支持单选题、多选题、判断题、填空题的自动识别
                </Text>
              </Col>
            </Row>
            
            {/* 识别结果预览 */}
            {recognizedQuestion && (
              <Card size="small" style={{ marginTop: 12, background: '#e6f7ff', border: '1px solid #91d5ff' }}>
                <Row gutter={16}>
                  <Col span={24}>
                    <Space style={{ marginBottom: 8 }}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <Text strong>识别成功！</Text>
                      <Tag color={QUESTION_TYPES.find(t => t.value === recognizedQuestion.question_type)?.color}>
                        {QUESTION_TYPES.find(t => t.value === recognizedQuestion.question_type)?.label}
                      </Tag>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Text><strong>题目：</strong>{recognizedQuestion.question_text}</Text>
                  </Col>
                  {recognizedQuestion.options && recognizedQuestion.options.length > 0 && (
                    <Col span={24} style={{ marginTop: 8 }}>
                      <Text><strong>选项：</strong></Text>
                      <div style={{ marginLeft: 16 }}>
                        {recognizedQuestion.options.map(opt => (
                          <Tag key={opt.key} style={{ margin: '2px' }}>{opt.key}. {opt.label}</Tag>
                        ))}
                      </div>
                    </Col>
                  )}
                  <Col span={24} style={{ marginTop: 8 }}>
                    <Text><strong>答案：</strong></Text>
                    <Tag color="green">{recognizedQuestion.correct_answer}</Tag>
                  </Col>
                </Row>
                <Button 
                  type="primary" 
                  size="small" 
                  style={{ marginTop: 12 }}
                  onClick={handleApplyRecognized}
                >
                  应用到表单
                </Button>
              </Card>
            )}
          </Card>
        )}
        
        <Divider style={{ margin: '16px 0' }}>
          {editingQuestion ? '编辑题目信息' : '手动填写 / 识别结果'}
        </Divider>

        <Form form={questionForm} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="subject" label="科目">
                <Select placeholder="选择科目" allowClear>
                  {SUBJECTS.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="grade" label="年级">
                <Select placeholder="选择年级" allowClear>
                  {GRADES.map(g => <Option key={g} value={g}>{g}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="question_type" label="题型" rules={[{ required: true }]}>
                <Select>
                  {QUESTION_TYPES.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="question_text"
            label="题目内容"
            rules={[{ required: true, message: '请输入题目内容' }]}
          >
            <TextArea rows={3} placeholder="请输入题目内容" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.question_type !== curr.question_type}>
            {({ getFieldValue }) => {
              const type = getFieldValue('question_type');
              if (type === 'single' || type === 'multiple') {
                return (
                  <>
                    <Divider>选项设置</Divider>
                    <Form.List name="options">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field, index) => (
                            <Row key={field.key} gutter={8} style={{ marginBottom: 8 }}>
                              <Col span={4}>
                                <Form.Item {...field} name={[field.name, 'key']} noStyle>
                                  <Input disabled style={{ textAlign: 'center' }} />
                                </Form.Item>
                              </Col>
                              <Col span={16}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'label']}
                                  rules={[{ required: true, message: '请输入选项内容' }]}
                                  noStyle
                                >
                                  <Input placeholder="选项内容" />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                {fields.length > 2 && (
                                  <Button type="link" danger onClick={() => remove(field.name)}>
                                    删除
                                  </Button>
                                )}
                              </Col>
                            </Row>
                          ))}
                          {fields.length < 6 && (
                            <Button
                              type="dashed"
                              onClick={() => {
                                const nextKey = String.fromCharCode(65 + fields.length);
                                add({ key: nextKey, label: '' });
                              }}
                              block
                            >
                              添加选项
                            </Button>
                          )}
                        </>
                      )}
                    </Form.List>
                  </>
                );
              }
              if (type === 'truefalse') {
                return (
                  <Alert
                    message="判断题选项固定为：对 / 错"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
              return null;
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="correct_answer"
                label="正确答案"
                rules={[{ required: true, message: '请输入正确答案' }]}
              >
                <Input placeholder="如: A 或 AB 或 对 或 黄河" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="difficulty" label="难度">
                <Select>
                  <Option value="easy">简单</Option>
                  <Option value="medium">中等</Option>
                  <Option value="hard">困难</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_shared" label="共享设置">
                <Select>
                  <Option value={0}>私有（仅自己可见）</Option>
                  <Option value={1}>共享给本校</Option>
                  <Option value={2}>公开共享</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="explanation" label="答案解析">
            <TextArea rows={2} placeholder="可选，填写答案解析" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入对话框 */}
      <Modal
        title="批量导入题目"
        open={importModalVisible}
        onOk={handleConfirmImport}
        onCancel={() => {
          setImportModalVisible(false);
          setPreviewResult(null);
          importForm.resetFields();
        }}
        width={900}
        okText="确认导入"
        cancelText="取消"
      >
        <Form form={importForm} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="subject" label="科目">
                <Select placeholder="选择科目" allowClear>
                  {SUBJECTS.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="grade" label="年级">
                <Select placeholder="选择年级" allowClear>
                  {GRADES.map(g => <Option key={g} value={g}>{g}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_shared" label="共享设置">
                <Select defaultValue={0}>
                  <Option value={0}>私有</Option>
                  <Option value={1}>共享本校</Option>
                  <Option value={2}>公开共享</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="text"
            label="粘贴题目内容"
            rules={[{ required: true, message: '请输入题目内容' }]}
            extra="支持从Word、文本文件等复制粘贴，系统会自动识别题目"
          >
            <TextArea
              rows={10}
              placeholder={`支持的格式示例：

1. 下列哪个是中国的首都？
A. 上海
B. 北京
C. 广州
D. 深圳
答案：B

2. 地球是太阳系中最大的行星。（判断题）
答案：错

3. 中国的母亲河是____。
答案：黄河`}
            />
          </Form.Item>

          <Button
            onClick={handlePreviewImport}
            loading={previewLoading}
            style={{ marginBottom: 16 }}
          >
            预览识别结果
          </Button>

          {previewResult && (
            <Card size="small" title={`识别结果：成功 ${previewResult.totalParsed} 题，失败 ${previewResult.totalErrors} 题`}>
              {previewResult.questions.map((q, index) => (
                <div key={index} style={{ marginBottom: 8 }}>
                  <Tag color={QUESTION_TYPES.find(t => t.value === q.question_type)?.color}>
                    {QUESTION_TYPES.find(t => t.value === q.question_type)?.label}
                  </Tag>
                  <Text ellipsis style={{ maxWidth: 500 }}>{q.question_text}</Text>
                  <Tag color="green" style={{ marginLeft: 8 }}>答案: {q.correct_answer}</Tag>
                </div>
              ))}
              {previewResult.errors.length > 0 && (
                <Alert
                  message="识别错误"
                  description={previewResult.errors.join('\n')}
                  type="warning"
                  style={{ marginTop: 8 }}
                />
              )}
            </Card>
          )}
        </Form>
      </Modal>

      {/* 推送题目对话框 */}
      <Modal
        title="推送题目到班级"
        open={pushModalVisible}
        onOk={handleSubmitPush}
        onCancel={() => setPushModalVisible(false)}
        okText="推送"
        cancelText="取消"
      >
        <Alert
          message={`已选择 ${selectedQuestionIds.length} 道题目`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={pushForm} layout="vertical">
          <Form.Item
            name="class_id"
            label="选择班级"
            rules={[{ required: true, message: '请选择班级' }]}
          >
            <Select placeholder="选择班级">
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>{cls.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="title" label="推送标题">
            <Input placeholder="可选，不填则使用默认标题" />
          </Form.Item>
          <Form.Item name="allow_class_start" valuePropName="checked">
            <Checkbox>允许班级端自行开始答题</Checkbox>
          </Form.Item>
          <Form.Item name="schedule_at" label="定时推送时间（留空表示立即）">
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
          <Alert
            message="说明"
            description="推送到桌面客户端后，若勾选“允许班级端自行开始答题”，学生端可直接点击“开始答题”。如选择时间，则会定时推送。"
            type="info"
            showIcon
          />
        </Form>
      </Modal>

      {/* 发起答题对话框 */}
      <Modal
        title="发起答题"
        open={sessionModalVisible}
        onOk={handleStartSession}
        onCancel={() => setSessionModalVisible(false)}
        width={700}
        okText="开始答题"
        cancelText="取消"
      >
        <Alert
          message={`已选择 ${selectedQuestionIds.length} 道题目`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={sessionForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="class_id"
                label="选择班级"
                rules={[{ required: true, message: '请选择班级' }]}
              >
                <Select placeholder="选择班级">
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title" label="答题标题">
                <Input placeholder="可选，如：第一单元测验" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>答题设置</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="answer_mode" label="答题模式">
                <Radio.Group>
                  <Radio value="normal">普通模式</Radio>
                  <Radio value="race">抢答模式</Radio>
                  <Radio value="timed">限时模式</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_attempts" label="答题次数">
                <Select>
                  <Option value={1}>每人1次</Option>
                  <Option value={2}>每人2次</Option>
                  <Option value={3}>每人3次</Option>
                  <Option value={0}>不限次数</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="time_per_question" label="每题时间(秒)">
                <InputNumber min={0} max={300} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="show_answer_after" label="答案显示时机">
                <Select>
                  <Option value="immediate">提交后立即显示</Option>
                  <Option value="all_submitted">全部提交后显示</Option>
                  <Option value="manual">手动显示</Option>
                  <Option value="end">答题结束后显示</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>积分设置</Divider>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="score_correct" label="答对得分">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="score_wrong" label="答错得分">
                <InputNumber min={-100} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="score_race_bonus" label="抢答加分">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      </Card>
    </div>
  );
};

export default DesktopQuizManager;
